import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { Subject, takeUntil, finalize } from 'rxjs';
import {
  ExternalClientService,
  ExternalClientListItemDto,
  ExternalClientResponseDto,
} from '../../services/externalclient.service';
import {
  ExternalClientTypeService,
  ExternalClientTypeListItemDto,
} from '../../services/externalclienttype.service';
import { ExternalClientDialogComponent } from '../../components/externalclient-dialog/externalclient-dialog.component';
import { YesornoDialogComponent } from '../../components/yesorno-dialog/yesorno-dialog.component';

interface StatusFilter {
  label: string;
  value: boolean | null;
}

@Component({
  selector: 'app-externalclient',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DatePipe,
    TableModule,
    ButtonModule,
    CardModule,
    InputTextModule,
    DropdownModule,
    TagModule,
    ToastModule,
    TooltipModule,
    DynamicDialogModule,
  ],
  providers: [DialogService, MessageService],
  template: `
    <p-toast></p-toast>

    <div class="page-container">
      <p-card styleClass="main-card">
        <ng-template pTemplate="header">
          <div class="card-header">
            <h2>
              <i class="pi pi-users"></i>
              Clientes Externos
            </h2>
            <p-button
              label="Novo Cliente"
              icon="pi pi-plus"
              (onClick)="openCreateDialog()"
              [disabled]="loading"
            ></p-button>
          </div>
        </ng-template>

        <ng-template pTemplate="content">
          <!-- Filtros -->
          <div class="filters-bar">
            <div class="filter-item">
              <span class="p-input-icon-left w-full">
                <i class="pi pi-search"></i>
                <input
                  pInputText
                  type="text"
                  [(ngModel)]="filterName"
                  (input)="applyFilters()"
                  placeholder="Buscar por nome..."
                  class="w-full"
                />
              </span>
            </div>
            <div class="filter-item">
              <p-dropdown
                [options]="typeFilterOptions"
                [(ngModel)]="filterTypeId"
                (onChange)="applyFilters()"
                placeholder="Todos os tipos"
                [showClear]="true"
                styleClass="w-full"
              ></p-dropdown>
            </div>
            <div class="filter-item">
              <p-dropdown
                [options]="statusFilterOptions"
                [(ngModel)]="filterStatus"
                (onChange)="applyFilters()"
                placeholder="Todos os status"
                styleClass="w-full"
              ></p-dropdown>
            </div>
          </div>

          @if (loading && dataSource.length === 0) {
            <div class="loading-container">
              <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
              <p>Carregando...</p>
            </div>
          } @else if (filteredData.length === 0 && dataSource.length === 0) {
            <div class="empty-state">
              <i class="pi pi-info-circle empty-icon"></i>
              <p>Nenhum cliente externo cadastrado.</p>
              <p-button
                label="Criar Primeiro Cliente"
                icon="pi pi-plus"
                (onClick)="openCreateDialog()"
                styleClass="p-button-outlined"
              ></p-button>
            </div>
          } @else if (filteredData.length === 0) {
            <div class="empty-state">
              <i class="pi pi-filter-slash empty-icon"></i>
              <p>Nenhum cliente encontrado com os filtros aplicados.</p>
            </div>
          } @else {
            <p-table
              [value]="filteredData"
              [paginator]="true"
              [rows]="10"
              [rowsPerPageOptions]="[5, 10, 20, 50]"
              [loading]="loading"
              responsiveLayout="scroll"
              styleClass="p-datatable-sm"
              [tableStyle]="{ 'min-width': '50rem' }"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th style="width: 4rem">#</th>
                  <th>Nome</th>
                  <th>Tipo</th>
                  <th>Prefixo</th>
                  <th style="width: 6rem">Status</th>
                  <th style="width: 10rem">Criado em</th>
                  <th style="width: 7rem" class="text-center">Ações</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-element>
                <tr>
                  <td>{{ element.id }}</td>
                  <td>
                    <strong>{{ element.name }}</strong>
                  </td>
                  <td>{{ element.typeName || '-' }}</td>
                  <td>
                    <code>{{ element.keyPrefix }}</code>
                  </td>
                  <td>
                    <p-tag
                      [value]="element.active ? 'Ativo' : 'Inativo'"
                      [severity]="element.active ? 'success' : 'danger'"
                      styleClass="status-tag"
                    ></p-tag>
                  </td>
                  <td>{{ element.createdAt | date: 'dd/MM/yyyy HH:mm' }}</td>
                  <td>
                    <div class="action-buttons">
                      <p-button
                        icon="pi pi-pencil"
                        severity="info"
                        [text]="true"
                        [rounded]="true"
                        pTooltip="Editar"
                        (onClick)="openEditDialog(element)"
                      ></p-button>
                      @if (element.active) {
                        <p-button
                          icon="pi pi-ban"
                          severity="danger"
                          [text]="true"
                          [rounded]="true"
                          pTooltip="Desativar"
                          (onClick)="confirmDeactivate(element)"
                        ></p-button>
                      }
                    </div>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }
        </ng-template>
      </p-card>
    </div>
  `,
  styles: [`
    .page-container {
      padding: 16px;
    }

    .card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 20px;
      background: var(--surface-50, #f8f9fa);
      border-bottom: 1px solid var(--surface-200, #dee2e6);
    }

    .card-header h2 {
      margin: 0;
      font-size: 1.25rem;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .filters-bar {
      display: flex;
      gap: 12px;
      padding: 12px 20px;
      background: var(--surface-0, #fff);
      border-bottom: 1px solid var(--surface-100, #f1f3f5);
      flex-wrap: wrap;
    }

    .filter-item {
      flex: 1;
      min-width: 180px;
    }

    .w-full {
      width: 100%;
    }

    .loading-container,
    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 48px 16px;
      gap: 12px;
      color: var(--text-color-secondary, #6c757d);
    }

    .empty-icon {
      font-size: 3rem;
      color: var(--surface-400, #ced4da);
    }

    .action-buttons {
      display: flex;
      justify-content: center;
      gap: 2px;
    }

    .text-center {
      text-align: center;
    }

    code {
      background: var(--surface-100, #f1f3f5);
      padding: 2px 6px;
      border-radius: 4px;
      font-size: 0.85rem;
    }

    :host ::ng-deep .main-card {
      border-radius: 8px;
      overflow: hidden;
    }

    :host ::ng-deep .main-card .p-card-body {
      padding: 0;
    }

    :host ::ng-deep .main-card .p-card-content {
      padding: 0;
    }

    :host ::ng-deep .status-tag {
      font-size: 0.75rem;
    }

    :host ::ng-deep .p-dropdown {
      min-width: 0;
    }
  `],
})
export class ExternalClientComponent implements OnInit, OnDestroy {
  dataSource: ExternalClientListItemDto[] = [];
  filteredData: ExternalClientListItemDto[] = [];
  loading = false;

  // Filtros
  filterName = '';
  filterTypeId: number | null = null;
  filterStatus: boolean | null = null;

  typeFilterOptions: { label: string; value: number | null }[] = [];
  statusFilterOptions: StatusFilter[] = [
    { label: 'Todos os status', value: null },
    { label: 'Ativo', value: true },
    { label: 'Inativo', value: false },
  ];

  private destroyed$ = new Subject<void>();

  constructor(
    private service: ExternalClientService,
    private typeService: ExternalClientTypeService,
    private dialogService: DialogService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  loadData(): void {
    this.loading = true;

    // Carrega clientes e tipos em paralelo
    this.service
      .list()
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (clients) => {
          this.dataSource = clients;
          this.applyFilters();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao carregar os clientes externos.',
            life: 5000,
          });
        },
      });

    this.typeService
      .list()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (types) => {
          this.typeFilterOptions = [
            { label: 'Todos os tipos', value: null },
            ...types.map((t) => ({ label: t.name, value: t.id })),
          ];
        },
      });
  }

  applyFilters(): void {
    let result = [...this.dataSource];

    if (this.filterName?.trim()) {
      const term = this.filterName.trim().toLowerCase();
      result = result.filter((c) => c.name.toLowerCase().includes(term));
    }

    if (this.filterTypeId != null) {
      result = result.filter((c) => c.typeId === this.filterTypeId);
    }

    if (this.filterStatus != null) {
      result = result.filter((c) => c.active === this.filterStatus);
    }

    this.filteredData = result;
    this.cdr.markForCheck();
  }

  openCreateDialog(): void {
    const ref = this.dialogService.open(ExternalClientDialogComponent, {
      header: 'Novo Cliente Externo',
      width: '500px',
      modal: true,
      closable: true,
      closeOnEscape: true,
    });

    ref.onClose.pipe(takeUntil(this.destroyed$)).subscribe((result: ExternalClientResponseDto | undefined) => {
      if (result) {
        this.dataSource = [this.mapResponseToListItem(result), ...this.dataSource];
        this.applyFilters();
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: `Cliente "${result.name}" adicionado à lista.`,
          life: 3000,
        });
      }
    });
  }

  openEditDialog(client: ExternalClientListItemDto): void {
    const ref = this.dialogService.open(ExternalClientDialogComponent, {
      header: 'Editar Cliente Externo',
      width: '500px',
      modal: true,
      closable: true,
      closeOnEscape: true,
      data: { client },
    });

    ref.onClose.pipe(takeUntil(this.destroyed$)).subscribe((result: ExternalClientResponseDto | undefined) => {
      if (result) {
        const idx = this.dataSource.findIndex((c) => c.id === result.id);
        if (idx >= 0) {
          this.dataSource[idx] = this.mapResponseToListItem(result);
        }
        this.applyFilters();
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: `Cliente "${result.name}" atualizado.`,
          life: 3000,
        });
      }
    });
  }

  confirmDeactivate(client: ExternalClientListItemDto): void {
    const ref = this.dialogService.open(YesornoDialogComponent, {
      header: 'Confirmação',
      width: '400px',
      data: {
        type: 'deactivateExternalClient',
        message: `Deseja desativar o cliente "${client.name}"?`,
      },
      closable: false,
    });

    ref.onClose.pipe(takeUntil(this.destroyed$)).subscribe((result) => {
      if (result?.confirmed) {
        this.deactivateClient(client);
      }
    });
  }

  private deactivateClient(client: ExternalClientListItemDto): void {
    this.service
      .deactivate(client.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          const idx = this.dataSource.findIndex((c) => c.id === client.id);
          if (idx >= 0) {
            this.dataSource[idx] = { ...this.dataSource[idx], active: false };
          }
          this.applyFilters();
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: `Cliente "${client.name}" desativado.`,
            life: 3000,
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao desativar o cliente.',
            life: 5000,
          });
        },
      });
  }

  private mapResponseToListItem(dto: ExternalClientResponseDto): ExternalClientListItemDto {
    return {
      id: dto.id,
      name: dto.name,
      active: dto.active,
      typeId: dto.typeId,
      typeName: dto.typeName,
      keyPrefix: dto.keyPrefix,
      createdAt: dto.createdAt,
    };
  }
}
